import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

interface AccountingAccounts {
  debtor: string;
  deposit: string;
  cash: string;
  card: string;
  transfer: string;
  other: string;
}

interface BuSchluessel {
  rate_19: string;
  rate_7: string;
  rate_0: string;
}

interface AccountingSettings {
  chart_id: "skr03" | "skr04";
  berater_nr: string;
  mandant_nr: string;
  accounts: AccountingAccounts;
  bu_schluessel: BuSchluessel;
}

interface TenantSettings {
  accounting: AccountingSettings;
}

export interface AccountingSettingsPanelProps {
  canWrite: boolean;
}

const ACCOUNT_FIELDS: { key: keyof AccountingAccounts; label: string; hint?: string }[] = [
  { key: "debtor", label: "Forderungen (Gegenkonto Erlös/Zahlung)" },
  { key: "deposit", label: "Anzahlungen" },
  { key: "cash", label: "Kasse (Barzahlung)" },
  { key: "card", label: "Karte" },
  { key: "transfer", label: "Bank (Überweisung)" },
  { key: "other", label: "Sonstige Zahlungsart" },
];

const BU_FIELDS: { key: keyof BuSchluessel; label: string }[] = [
  { key: "rate_19", label: "19 % USt" },
  { key: "rate_7", label: "7 % USt" },
  { key: "rate_0", label: "0 % / steuerfrei" },
];

export function AccountingSettingsPanel({ canWrite }: AccountingSettingsPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartId, setChartId] = useState<"skr03" | "skr04">("skr03");
  const [beraterNr, setBeraterNr] = useState("");
  const [mandantNr, setMandantNr] = useState("");
  const [accounts, setAccounts] = useState<AccountingAccounts>({
    debtor: "",
    deposit: "",
    cash: "",
    card: "",
    transfer: "",
    other: "",
  });
  const [buSchluessel, setBuSchluessel] = useState<BuSchluessel>({
    rate_19: "9",
    rate_7: "8",
    rate_0: "0",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      const accounting = settings.accounting;
      setChartId(accounting.chart_id);
      setBeraterNr(accounting.berater_nr);
      setMandantNr(accounting.mandant_nr);
      setAccounts(accounting.accounts);
      setBuSchluessel(accounting.bu_schluessel);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/accounting", {
        berater_nr: beraterNr.trim(),
        mandant_nr: mandantNr.trim(),
        accounts,
        bu_schluessel: buSchluessel,
      });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  function updateAccount(key: keyof AccountingAccounts, value: string) {
    setAccounts((prev) => ({ ...prev, [key]: value }));
  }

  function updateBu(key: keyof BuSchluessel, value: string) {
    setBuSchluessel((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <Card title="Buchhaltungs-Export" bodyClassName="space-y-6">
      <p className="text-sm text-label-secondary">
        Systemkonten und BU-Schlüssel für den Buchhaltungs-Export (CSV und DATEV). Der
        Kontenrahmen ({chartId.toUpperCase()}) wird beim Import des Kontenrahmens gesetzt.
      </p>

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <form onSubmit={(e) => void onSave(e)} className="space-y-6">
          <div>
            <h3 className="mb-3 text-sm font-medium">DATEV-Stammdaten</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block text-label-secondary">Berater-Nr.</span>
                <input
                  className="eh-input tabular-nums"
                  value={beraterNr}
                  onChange={(e) => setBeraterNr(e.target.value)}
                  disabled={!canWrite}
                  maxLength={20}
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-label-secondary">Mandanten-Nr.</span>
                <input
                  className="eh-input tabular-nums"
                  value={mandantNr}
                  onChange={(e) => setMandantNr(e.target.value)}
                  disabled={!canWrite}
                  maxLength={20}
                />
              </label>
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium">Buchungskonten</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {ACCOUNT_FIELDS.map(({ key, label }) => (
                <label key={key} className="text-sm">
                  <span className="mb-1 block text-label-secondary">{label}</span>
                  <input
                    className="eh-input tabular-nums"
                    value={accounts[key]}
                    onChange={(e) => updateAccount(key, e.target.value)}
                    disabled={!canWrite}
                    maxLength={20}
                  />
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-medium">BU-Schlüssel (DATEV)</h3>
            <p className="mb-3 text-xs text-label-tertiary">
              Steuerschlüssel für den Export, abhängig vom Rechnungs-Steuersatz.
            </p>
            <div className="grid gap-3 sm:grid-cols-3">
              {BU_FIELDS.map(({ key, label }) => (
                <label key={key} className="text-sm">
                  <span className="mb-1 block text-label-secondary">{label}</span>
                  <input
                    className="eh-input tabular-nums"
                    value={buSchluessel[key]}
                    onChange={(e) => updateBu(key, e.target.value)}
                    disabled={!canWrite}
                    maxLength={10}
                  />
                </label>
              ))}
            </div>
          </div>

          {canWrite ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button type="submit" loading={saving} disabled={saving}>
                Einstellungen speichern
              </Button>
              {success ? (
                <p className="text-sm text-status-green">Buchhaltungs-Einstellungen gespeichert.</p>
              ) : null}
            </div>
          ) : null}
        </form>
      )}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
    </Card>
  );
}
